import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { downloadFile, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { X, FileDown, Users, Mail, Loader2, Eye } from "lucide-react";

/**
 * Shared membership-card viewer/printer/downloader modal.
 *
 * Mounted via React Portal directly into <body> so no ancestor's CSS
 * transform/contain/filter can break the `position: fixed` overlay
 * (the dashboard's `<main>` uses a `page-fade-in` animation with a
 * `transform` keyframe — that creates a containing block).
 *
 * Modes:
 *   "admin"    — shows the "Email all cards" button in the family panel.
 *   "customer" — hides admin-only controls (used in CustomerDashboard).
 *
 * Performance: after the roster fetch, primary + every sub-member front
 * PNG is pre-warmed in the background — subsequent View clicks resolve
 * from an in-memory blob cache (instant, no re-render).
 *
 * Props:
 *   membership: { membership_id, member_id?, full_name?, email? }
 *   onClose:    () => void
 *   mode:       "admin" | "customer" (default "admin")
 */
export default function MembershipCardModal({ membership, onClose, mode = "admin", initialMemberId = null, initialSide = "front" }) {
  const [side, setSide] = useState(initialSide);
  const [activeMemberId, setActiveMemberId] = useState(initialMemberId);
  const [activeMemberName, setActiveMemberName] = useState(membership.full_name || "");

  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [emailing, setEmailing] = useState(false);

  // In-memory blob cache keyed by `${memberId|primary}::${side}`. Persists
  // for the lifetime of this modal; revoked en-masse on unmount.
  const cacheRef = useRef(new Map());

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const cache = cacheRef.current;
    return () => {
      document.body.style.overflow = prev;
      // Revoke any object URLs we minted while open.
      cache.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
      });
      cache.clear();
    };
  }, []);

  // Roster + warm-cache: fetch the cards list once, then in the background
  // pre-fetch each member's FRONT PNG so per-member View clicks resolve
  // instantly. Back-side PNGs warm lazily on first click.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/memberships/by-id/${membership.membership_id}/cards`);
        if (cancelled) return;
        const list = r.data?.cards || [];
        setCards(list);
        // Pick whichever card the modal was OPENED on (initialMemberId via
        // props → activeMemberId state) so the header name stays in sync
        // with the previewed card. Falls back to the primary entry only
        // when the caller didn't pre-select anyone.
        const active = activeMemberId
          ? list.find((c) => c.member_id === activeMemberId)
          : list.find((c) => c.is_primary) || list[0];
        if (active) setActiveMemberName(active.full_name || membership.full_name || "");
        // Background warm — pre-fetch BOTH sides for every member so that
        // the per-row Front/Back quick-switch resolves instantly. Errors
        // are swallowed; the on-demand preview effect retries.
        for (const c of list) {
          for (const sideKind of ["front", "back"]) {
            const key = `${c.is_primary ? "primary" : c.member_id}::${sideKind}`;
            if (cacheRef.current.has(key)) continue;
            const endpoint = sideKind === "back" ? "card-back.png" : "card.png";
            api.get(
              `/memberships/by-id/${membership.membership_id}/${endpoint}`,
              { responseType: "blob", params: c.is_primary ? undefined : { member_id: c.member_id } },
            ).then((res) => {
              if (cancelled) return;
              const u = URL.createObjectURL(res.data);
              cacheRef.current.set(key, u);
              // If this is what's currently displayed, swap in immediately.
              const isActive = c.is_primary ? !activeMemberId : c.member_id === activeMemberId;
              if (isActive && side === sideKind) setBlobUrl(u);
            }).catch(() => { /* on-demand effect will retry */ });
          }
        }
      } catch (e) {
        if (!cancelled) console.error("Cards roster fetch failed", e);
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membership.membership_id]);

  // Preview fetch — checks cache first, falls back to network. Each
  // (member, side) tuple is fetched at most once per modal lifetime.
  useEffect(() => {
    let cancelled = false;
    const key = `${activeMemberId || "primary"}::${side}`;
    const cached = cacheRef.current.get(key);
    if (cached) {
      setBlobUrl(cached);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const endpoint = side === "back" ? "card-back.png" : "card.png";
    const params = activeMemberId ? { member_id: activeMemberId } : undefined;
    (async () => {
      try {
        const res = await api.get(
          `/memberships/by-id/${membership.membership_id}/${endpoint}`,
          { responseType: "blob", params },
        );
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        cacheRef.current.set(key, url);
        setBlobUrl(url);
      } catch (e) {
        if (!cancelled) setError(apiErr(e, "Failed to load card"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [membership.membership_id, side, activeMemberId]);

  const baseStem = useMemo(
    () => `NPC-Card-${activeMemberId || membership.member_id || membership.membership_id}`,
    [activeMemberId, membership.member_id, membership.membership_id],
  );
  const previewParam = activeMemberId ? `?member_id=${encodeURIComponent(activeMemberId)}` : "";

  const downloadBothPdf = async () => {
    try {
      await downloadFile(
        `/memberships/by-id/${membership.membership_id}/card-both.pdf${previewParam}`,
        `${baseStem}-Both.pdf`,
      );
    } catch (e) {
      toast.error(apiErr(e, "Failed to download both sides"));
    }
  };

  const downloadMemberBoth = async (card) => {
    setDownloadingId(card.member_id);
    try {
      const params = card.is_primary ? "" : `?member_id=${encodeURIComponent(card.member_id)}`;
      const safeName = (card.full_name || card.member_id || "member").replace(/[^A-Za-z0-9._-]/g, "-");
      await downloadFile(
        `/memberships/by-id/${membership.membership_id}/card-both.pdf${params}`,
        `NPC-Card-${card.member_id}-${safeName}.pdf`,
      );
    } catch (e) {
      toast.error(apiErr(e, "Failed to download card"));
    } finally {
      setDownloadingId(null);
    }
  };

  const viewMember = (card, nextSide = "front") => {
    setActiveMemberId(card.is_primary ? null : card.member_id);
    setActiveMemberName(card.full_name || "");
    setSide(nextSide);
  };

  const emailAllCards = async () => {
    const totalCards = cards.length || 1;
    if (!window.confirm(
      `Email all ${totalCards} membership card(s) to ${membership.email || "the primary member"}?\n\n` +
      `Each card will be attached as a front+back PDF.`,
    )) return;
    setEmailing(true);
    try {
      const r = await api.post("/admin/email/resend-membership", {
        membership_id: membership.membership_id,
        force: true,
      });
      if (r.data?.skipped) toast.info("Already sent — used force=true");
      else toast.success(`Email queued · ${totalCards} card${totalCards > 1 ? "s" : ""} attached`);
    } catch (e) {
      toast.error(apiErr(e, "Failed to send email"));
    } finally {
      setEmailing(false);
    }
  };

  const currentMemberId = activeMemberId || (cards.find((c) => c.is_primary)?.member_id) || membership.member_id || "";
  const isAdmin = mode === "admin";

  const modal = (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="admin-card-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-[#E1E8F0] shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B7A]">Membership card</div>
            <h3 className="font-display text-xl font-bold text-[#0F2540] mt-0.5 truncate">
              {activeMemberName || membership.full_name || membership.membership_id}
            </h3>
            <p className="text-xs text-[#5C6B7A] mt-1 font-mono">
              {currentMemberId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#5C6B7A] hover:text-[#0F2540] p-1 -m-1 rounded-md shrink-0"
            aria-label="Close"
            data-testid="admin-card-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          <div className="rounded-xl bg-[#F4F7FB] border border-[#E1E8F0] p-3 flex items-center justify-center min-h-[200px]">
            {loading && (
              <div className="text-sm text-[#5C6B7A] py-10" data-testid="admin-card-loading">
                Generating card…
              </div>
            )}
            {error && (
              <div className="text-sm text-[#E63946] py-10" data-testid="admin-card-error">{error}</div>
            )}
            {!loading && !error && blobUrl && (
              <img
                src={blobUrl}
                alt={side === "back" ? "NPC Membership Card (back)" : "NPC Membership Card"}
                className="w-full max-w-[400px] h-auto rounded-xl shadow-md"
                data-testid="admin-card-img"
              />
            )}
          </div>

          <div className="flex items-center justify-between" data-testid="admin-card-side-bar">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B7A]">Side</span>
            <div className="inline-flex rounded-full bg-[#F0F4FA] p-1 border border-[#E1E8F0]">
              <button
                type="button"
                onClick={() => setSide("front")}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${side === "front" ? "bg-[#0F2540] text-white shadow-sm" : "text-[#0F2540] hover:bg-white"}`}
                data-testid="admin-card-side-front"
              >
                Front
              </button>
              <button
                type="button"
                onClick={() => setSide("back")}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${side === "back" ? "bg-[#0F2540] text-white shadow-sm" : "text-[#0F2540] hover:bg-white"}`}
                data-testid="admin-card-side-back"
              >
                Back
              </button>
            </div>
          </div>

          {(cards.length > 1 || cardsLoading) && (
            <div className="rounded-xl border border-[#E1E8F0] bg-white" data-testid="admin-card-family-panel">
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E1E8F0] bg-[#F8FAFD] rounded-t-xl">
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 text-[#0F2540] shrink-0" />
                  <h4 className="text-sm font-semibold text-[#0F2540] truncate">
                    All cards {!cardsLoading && cards.length > 0 && (
                      <span className="text-[11px] text-[#5C6B7A] font-normal ml-1">· {cards.length}</span>
                    )}
                  </h4>
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={emailAllCards}
                    disabled={emailing || cardsLoading || cards.length === 0 || !membership.email}
                    className="btn-primary !text-xs inline-flex items-center gap-1.5 disabled:opacity-50 shrink-0"
                    title={membership.email ? `Email all cards to ${membership.email}` : "Membership has no email on file"}
                    data-testid="admin-card-email-all-btn"
                  >
                    {emailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    {emailing ? "Sending…" : "Email all"}
                  </button>
                )}
              </div>
              <div className="divide-y divide-[#E1E8F0]" data-testid="admin-card-family-list">
                {cardsLoading && (
                  <div className="px-4 py-6 text-sm text-[#5C6B7A] text-center">Loading…</div>
                )}
                {!cardsLoading && cards.map((c) => {
                  const isActive = (c.is_primary && !activeMemberId) || c.member_id === activeMemberId;
                  return (
                    <div
                      key={c.member_id}
                      className={`flex items-center justify-between gap-3 px-4 py-2.5 ${isActive ? "bg-[#EAF1FA]" : ""}`}
                      data-testid={`admin-card-family-row-${c.member_id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[#0F2540] truncate">
                            {c.full_name || "—"}
                          </span>
                          {c.is_primary && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1E4F87] text-white font-semibold uppercase tracking-wider">Primary</span>
                          )}
                          {c.relation && !c.is_primary && (
                            <span className="text-[11px] text-[#5C6B7A] capitalize">· {c.relation}</span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#5C6B7A] font-mono mt-0.5">
                          {c.member_id}{c.uhid ? ` · UHID ${c.uhid}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <div
                          className="inline-flex rounded-full border border-[#1E4F87] overflow-hidden text-[11px] leading-none"
                          role="group"
                          aria-label={`View ${c.full_name || c.member_id} card`}
                        >
                          {["front", "back"].map((s) => {
                            const isOn = isActive && side === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => viewMember(c, s)}
                                className={`px-2.5 py-1 inline-flex items-center gap-1 transition-colors ${isOn ? "bg-[#1E4F87] text-white" : "text-[#1E4F87] hover:bg-[#EAF1FA]"}`}
                                title={`View ${s === "back" ? "back" : "front"}`}
                                data-testid={`admin-card-family-view-${s}-${c.member_id}`}
                              >
                                {s === "front" ? <Eye className="w-3 h-3" /> : null}
                                {s === "front" ? "Front" : "Back"}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={() => downloadMemberBoth(c)}
                          disabled={downloadingId === c.member_id}
                          className="text-xs px-2.5 py-1 rounded-full border border-[#E1E8F0] text-[#0F2540] hover:bg-[#F4F7FB] inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`admin-card-family-pdf-${c.member_id}`}
                        >
                          {downloadingId === c.member_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                          PDF
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-[#E1E8F0] bg-[#FAFBFD] rounded-b-2xl shrink-0">
          <button
            onClick={onClose}
            className="btn-ghost !text-sm"
            data-testid="admin-card-cancel-btn"
          >
            Close
          </button>
          <button
            onClick={downloadBothPdf}
            className="btn-primary !text-sm inline-flex items-center gap-2"
            title="2-page PDF: front + back, ready for double-sided card printing"
            data-testid="admin-card-both-btn"
          >
            <FileDown className="w-4 h-4" /> Front + Back PDF
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}
